import { useEffect, useState } from 'react';
import { getLabData } from './api';
import SectionHeader from './SectionHeader';

/**
 * Парсит референс вида "3.3-5.5", "3.3–5.5", "< 40", "> 0".
 * Возвращает { min, max } или null если не распознан.
 */
function parseReference(ref) {
  if (!ref) return null;
  const s = ref.trim();

  // Диапазон: "3.3-5.5" или "3.3–5.5" или "3.3 - 5.5"
  const rangeMatch = s.match(/^(-?[\d.,]+)\s*[–\-]\s*(-?[\d.,]+)$/);
  if (rangeMatch) {
    const lo = parseFloat(rangeMatch[1].replace(',', '.'));
    const hi = parseFloat(rangeMatch[2].replace(',', '.'));
    if (!isNaN(lo) && !isNaN(hi)) return { min: lo, max: hi };
  }

  // Верхняя граница: "< 40" или "≤ 40"
  const upperMatch = s.match(/^[<≤]\s*(-?[\d.,]+)$/);
  if (upperMatch) {
    const hi = parseFloat(upperMatch[1].replace(',', '.'));
    if (!isNaN(hi)) return { min: -Infinity, max: hi };
  }

  // Нижняя граница: "> 0" или "≥ 0"
  const lowerMatch = s.match(/^[>≥]\s*(-?[\d.,]+)$/);
  if (lowerMatch) {
    const lo = parseFloat(lowerMatch[1].replace(',', '.'));
    if (!isNaN(lo)) return { min: lo, max: Infinity };
  }

  return null;
}

/**
 * Определяет статус: 'normal' | 'high' | 'low' | 'unknown'
 */
function getStatus(value, ref) {
  const parsed = parseReference(ref);
  if (!parsed) return 'unknown';
  const num = parseFloat(String(value).replace(',', '.'));
  if (isNaN(num)) return 'unknown';
  if (num < parsed.min) return 'low';
  if (num > parsed.max) return 'high';
  return 'normal';
}

const STATUS_CONFIG = {
  normal: { label: '✓ норма',  color: '#3a7d44' },
  high:   { label: '↑ выше',   color: '#C74800' },
  low:    { label: '↓ ниже',   color: '#1a6fb5' },
  unknown: { label: '',        color: '#888'     },
};

const FILTER_OPTIONS = [
  { label: 'не норма', value: 'abnormal' },
  { label: 'все', value: 'all' },
];

export default function LabBlock({ petId, docs, onDocsClick }) {
  const [indicators, setIndicators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('abnormal');

  useEffect(() => {
    getLabData(petId)
      .then(setIndicators)
      .catch(() => setIndicators([]))
      .finally(() => setLoading(false));
  }, [petId]);

  if (loading) return null;

  const hasAbnormalResults = (ind) => {
    return ind.results.some(res => {
      const status = getStatus(res.value, ind.reference);
      return status !== 'normal' && status !== 'unknown';
    });
  };

  const filteredIndicators = indicators.filter(ind => {
    if (filter === 'all') return true;
    return hasAbnormalResults(ind);
  });

  const filterTabs = (
    <div className="visits-period-tabs" style={{ marginBottom: 12 }}>
      {FILTER_OPTIONS.map(opt => (
        <button
          key={opt.value}
          className={`visits-tab${filter === opt.value ? ' visits-tab-active' : ''}`}
          onClick={() => setFilter(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );

  return (
    <section className="card-section" id="section-lab">
      <SectionHeader
        title="Анализы"
        periodMode="none"
        sectionKey="lab"
        docs={docs}
        onDocsClick={onDocsClick}
      />

      {filterTabs}

      {filteredIndicators.length === 0 ? (
        <p style={{ color: '#888', fontSize: 14, margin: 0 }}>
          {filter === 'abnormal' ? 'Все анализы в норме' : 'Анализы ещё не добавлены'}
        </p>
      ) : (
        filteredIndicators.map((ind) => (
          <div key={ind.id} className="lab-indicator">
            <div className="lab-indicator-header">
              <span className="lab-indicator-name">🔬 {ind.name}</span>
              <span className="lab-indicator-unit">{ind.unit}</span>
            </div>
            {ind.reference && (
              <div className="lab-reference">Референс: {ind.reference}</div>
            )}
            {ind.results.length === 0 ? (
              <div className="lab-no-results">Результатов пока нет</div>
            ) : (
              <div className="lab-results">
                {ind.results.map((res) => {
                  const status = getStatus(res.value, ind.reference);
                  const cfg = STATUS_CONFIG[status];
                  return (
                    <div key={res.id} className="lab-result-row">
                      <span className="lab-result-date">{res.date}</span>
                      <span className="lab-result-value">{res.value} {ind.unit}</span>
                      {cfg.label && (
                        <span className="lab-result-status" style={{ color: cfg.color }}>
                          {cfg.label}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))
      )}
    </section>
  );
}
